import { useMemo, useState } from 'react';
import { Panel, TextInput, Select, Button, Checkbox, IconButton, Notice, Dialog } from '@origo/origo-ui';
import { BoundingBoxIcon, MagnifyingGlassPlusIcon } from '@phosphor-icons/react';
import type { Område } from '@origo/seksjon';
import { useMapViewContext } from '@origo/map-context';
import { useDelområdeMapSelection } from '../hooks/useDelområdeMapSelection';
import { formatAreal } from '../../planer/utils/planUtils';
import styles from './GroupEditorPanel.module.css';

export interface GroupEditorInitial {
    /** Når satt: rediger eksisterende gruppe (delomradetype er låst). */
    gruppe?: { objectId: number; id: string; navn: string };
    /** Forhåndsvalgt delomradetype. Påkrevd ved redigering, valgfri ved opprettelse. */
    delomradetype?: string;
    /** Forhåndsvalgte medlems-LOKALID-er. */
    initialMembers?: readonly string[];
}

export interface GroupEditorSubmit {
    delomradetype: string;
    navn: string;
    addMembers: string[];
    removeMembers: string[];
}

interface Props {
    initial: GroupEditorInitial | null;
    /**
     * Delomradetypene tilgjengelig i planen — kreves når brukeren oppretter
     * en ny gruppe og må velge type. Hver type har sine `children` (delområder).
     */
    availableTypes: readonly Område[];
    /** Eksisterende medlems-LOKALID-er (kun for redigerings-modus). */
    currentMembers?: readonly string[];
    /** Andre delområder som allerede er medlem av en annen gruppe innen samme type — disabled. */
    blockedMembersByType?: Record<string, readonly string[]>;
    onSubmit: (values: GroupEditorSubmit) => Promise<void> | void;
    onDelete?: () => Promise<void> | void;
    onCancel: () => void;
    error?: string | null;
}

export function GroupEditorPanel({
    initial,
    availableTypes,
    currentMembers = [],
    blockedMembersByType = {},
    onSubmit,
    onDelete,
    onCancel,
    error,
}: Readonly<Props>) {
    const isEditing = !!initial?.gruppe;
    const typeLocked = isEditing;

    // Panelet remountes per gruppe (key i AdminPage), så lazy init fra props er
    // korrekt og unngår at en refetch av currentMembers overskriver brukerens
    // pågående valg.
    const [delomradetype, setDelomradetype] = useState<string>(initial?.delomradetype ?? '');
    const [navn, setNavn] = useState<string>(initial?.gruppe?.navn ?? '');
    const [selected, setSelected] = useState<Set<string>>(() =>
        initial?.gruppe ? new Set(currentMembers) : new Set(initial?.initialMembers ?? [])
    );
    const [submitting, setSubmitting] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const { mapView } = useMapViewContext();

    const activeType = useMemo(
        () => availableTypes.find((t) => t.id === delomradetype),
        [availableTypes, delomradetype]
    );
    const delområderInType: readonly Område[] = activeType?.children ?? [];
    const blocked = useMemo(
        () => new Set(blockedMembersByType[delomradetype] ?? []),
        [blockedMembersByType, delomradetype]
    );

    const candidates = useMemo(
        () => delområderInType.map((d) => ({ id: d.id, geometry: d.geometry })),
        [delområderInType]
    );

    // Valgte delområder vises øverst. delområderInType kommer allerede sortert
    // etter NavPanel-reglene (feltnavn naturlig, deretter areal synkende) fra
    // useAdminAreas; Array.sort er stabil, så den rekkefølgen bevares innen hver
    // gruppe (valgt / ikke valgt).
    const sortedDelområder = useMemo(
        () => [...delområderInType].sort(
            (a, b) => Number(selected.has(b.id)) - Number(selected.has(a.id))
        ),
        [delområderInType, selected]
    );

    const { isDrawingRectangle, startRectangle, cancelRectangle, zoomToSelection, zoomTo } =
        useDelområdeMapSelection({
            mapView,
            candidates,
            selected,
            blocked,
            onChange: setSelected,
            enabled: true,
        });

    function changeType(next: string) {
        setDelomradetype(next);
        // Bytt av type → tøm medlemsvalg (de gjelder en annen type).
        setSelected(new Set());
    }

    function toggleMember(id: string) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    async function handleSubmit() {
        const trimmed = navn.trim();
        if (!trimmed || !delomradetype || selected.size === 0) return;
        const before = new Set(currentMembers);
        const addMembers = [...selected].filter((id) => !before.has(id));
        const removeMembers = [...before].filter((id) => !selected.has(id));
        setSubmitting(true);
        try {
            await onSubmit({ delomradetype, navn: trimmed, addMembers, removeMembers });
        } finally {
            setSubmitting(false);
        }
    }

    function handleDelete() {
        if (!onDelete) return;
        setDeleteDialogOpen(true);
    }

    async function handleDeleteConfirm() {
        if (!onDelete) return;
        setSubmitting(true);
        try {
            await onDelete();
        } finally {
            setSubmitting(false);
            setDeleteDialogOpen(false);
        }
    }

    const typeOptions = useMemo(
        () => availableTypes.map((t) => ({ value: t.id, label: t.navn })),
        [availableTypes]
    );

    const submitDisabled =
        submitting || !delomradetype || !navn.trim() || selected.size === 0;

    const candidatesWithGeometry = candidates.some((c) => c.geometry);

    return (
        <>
            <div className={styles.root}>
                <Panel
                    heading={isEditing ? 'Rediger delområdegruppe' : 'Ny delområdegruppe'}
                    closable
                    onClose={onCancel}
                    flush
                    footer={
                        <div className={styles.footerInner}>
                            {isEditing && onDelete && (
                                <Button
                                    variant="danger"
                                    onClick={handleDelete}
                                    disabled={submitting}
                                    style={{ marginRight: 'auto' }}
                                >
                                    Slett gruppe
                                </Button>
                            )}
                            <Button variant="secondary" onClick={onCancel} disabled={submitting}>
                                Avbryt
                            </Button>
                            <Button variant="primary" onClick={handleSubmit} disabled={submitDisabled}>
                                {isEditing ? 'Lagre' : 'Opprett'}
                            </Button>
                        </div>
                    }
                >
                    <div className={styles.content}>
                        <section className={styles.section}>
                            <h3 className={styles.sectionHeading}>Egenskaper</h3>
                            <Select
                                label="Delområdetype"
                                value={delomradetype}
                                onChange={(e) => changeType(e.target.value)}
                                disabled={typeLocked}
                                options={[{ value: '', label: 'Velg type…' }, ...typeOptions]}
                            />
                            <TextInput
                                label="Navn"
                                value={navn}
                                placeholder="F.eks. Boliger med høydebegrensning"
                                onChange={(e) => setNavn(e.target.value)}
                            />
                        </section>

                        {delomradetype && (
                            <section className={styles.selectSection}>
                                <h3 className={styles.sectionHeading}>Velg delområder</h3>
                                <p className={styles.sectionHelp}>
                                    Klikk et delområde i kartet for å velge det, eller dra en firkant for å
                                    velge flere. Hold <kbd className={styles.kbd}>Shift</kbd> mens du drar
                                    for å fjerne. Du kan også huke av i listen under.
                                </p>
                                <div className={styles.mapTools}>
                                    <Button
                                        variant={isDrawingRectangle ? 'primary' : 'secondary'}
                                        size="sm"
                                        leftIcon={<BoundingBoxIcon size={16} />}
                                        onClick={isDrawingRectangle ? cancelRectangle : startRectangle}
                                        disabled={!candidatesWithGeometry}
                                    >
                                        {isDrawingRectangle ? 'Avbryt firkant' : 'Velg med firkant'}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        leftIcon={<MagnifyingGlassPlusIcon size={16} />}
                                        onClick={zoomToSelection}
                                        disabled={selected.size === 0}
                                    >
                                        Zoom til valgte
                                    </Button>
                                </div>

                                <div className={styles.memberCount}>
                                    Medlemmer ({selected.size} av {delområderInType.length})
                                </div>
                                <div className={styles.memberList}>
                                    {delområderInType.length === 0 ? (
                                        <Notice variant="info">Ingen delområder i denne typen.</Notice>
                                    ) : (
                                        sortedDelområder.map((d) => {
                                            const isBlocked = blocked.has(d.id) && !selected.has(d.id);
                                            const base = d.code ? `${d.code} · ${d.navn}` : d.navn;
                                            const label = isBlocked ? `${base} (medlem av annen gruppe)` : base;
                                            const areal = formatAreal(d.areaM2);
                                            return (
                                                <div key={d.id} className={styles.memberRow}>
                                                    <Checkbox
                                                        className={styles.memberCheckbox}
                                                        label={label}
                                                        checked={selected.has(d.id)}
                                                        disabled={isBlocked}
                                                        onChange={() => toggleMember(d.id)}
                                                    />
                                                    {areal && <span className={styles.memberAreal}>({areal})</span>}
                                                    <IconButton
                                                        className={styles.memberZoom}
                                                        icon={<MagnifyingGlassPlusIcon size={14} />}
                                                        label={`Zoom til ${d.navn}`}
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => zoomTo(d.id)}
                                                        disabled={!d.geometry}
                                                    />
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </section>
                        )}

                        {error && <Notice variant="danger">{error}</Notice>}
                    </div>
                </Panel>
            </div>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                title="Slett gruppe"
                footer={
                    <div className={styles.dialogFooter}>
                        <Button variant="secondary" onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
                            Avbryt
                        </Button>
                        <Button variant="danger" onClick={handleDeleteConfirm} disabled={submitting}>
                            Slett gruppe
                        </Button>
                    </div>
                }
            >
                <p>Er du sikker på at du vil slette denne gruppen? Veiledningspunktene som er skrevet på gruppen blir også fjernet.</p>
            </Dialog>
        </>
    );
}
